/*
  Interaction event tracking.

  Pushes the custom events from the tracking sheet to window.dataLayer. GA4 funnel
  events (view_item, add_to_cart, begin_checkout, purchase, ...) come from Shopify's
  own analytics and are not duplicated here - the quick buy events fire *on top of*
  the existing add_to_cart, as specified in the sheet.

  QA: open the store once with ?tracklog=1. That tab keeps every event in
  sessionStorage and reprints the full list in the console after each refresh
  or navigation. ?tracklog=0 clears it. window.trackingDebug = true still logs
  the next push only, and does not survive a refresh.
*/
(function () {
  window.dataLayer = window.dataLayer || [];

  var LOG_KEY = 'skinome.trackingLog';
  var LOG_FLAG = 'skinome.trackingLogOn';
  var LOG_MAX = 200;

  function queryFlag(name) {
    var match = window.location.search.match(new RegExp('[?&]' + name + '=([^&]*)'));
    return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : '';
  }

  function logEnabled() {
    try {
      if (queryFlag('tracklog') === '0') {
        sessionStorage.removeItem(LOG_FLAG);
        sessionStorage.removeItem(LOG_KEY);
        return false;
      }
      if (queryFlag('tracklog') === '1') sessionStorage.setItem(LOG_FLAG, '1');
      return sessionStorage.getItem(LOG_FLAG) === '1';
    } catch (e) {
      return false;
    }
  }

  function storedEvents() {
    try {
      var parsed = JSON.parse(sessionStorage.getItem(LOG_KEY) || '[]');
      return parsed && parsed.length ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function printEntry(entry, index) {
    console.log('[tracking ' + (index + 1) + '] ' + entry.event + '  ' + entry.page, entry.payload);
  }

  function remember(payload) {
    if (window.trackingDebug && !logEnabled()) console.log('[tracking]', payload);
    if (!logEnabled()) return;

    var events = storedEvents();
    events.push({
      event: payload.event || '(no name)',
      page: window.location.pathname,
      at: new Date().toISOString(),
      payload: payload
    });
    if (events.length > LOG_MAX) events = events.slice(events.length - LOG_MAX);
    try { sessionStorage.setItem(LOG_KEY, JSON.stringify(events)); } catch (e) {}
    printEntry(events[events.length - 1], events.length - 1);
  }

  function replay() {
    if (!logEnabled()) return;
    var events = storedEvents();
    if (!events.length) {
      console.log('[tracking] on. No events yet. This list survives refresh in this tab.');
      return;
    }
    console.groupCollapsed('[tracking] ' + events.length + ' events kept across refresh');
    for (var i = 0; i < events.length; i++) printEntry(events[i], i);
    console.groupEnd();
  }

  window.trackingLog = function () {
    var events = storedEvents();
    console.table(events.map(function (entry) {
      return { event: entry.event, page: entry.page, at: entry.at };
    }));
    return events;
  };

  var template = (window.theme && window.theme.template) || '';
  var collectionTitle = (window.theme && window.theme.collectionTitle) || '';
  var collectionHandle = (window.theme && window.theme.collectionHandle) || '';
  // Shopify prefixes non-primary locales onto every path ("/en/products/x"), which
  // would split one link into a row per language. Strip it so link_url is comparable.
  var localeRoot = (window.theme && window.theme.routes && window.theme.routes.root) || '/';

  // Selectors for markup the theme does not render can be corrected from Theme
  // settings > Scripts without a deploy. An empty or whitespace setting keeps the
  // default below, so a blank field can never silently disable an event.
  var overrides = (window.theme && window.theme.trackingSelectors) || {};
  function sel(key, fallback) {
    var value = overrides[key];
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }


  // First matching rule wins, so the more specific areas are listed first.
  // area      - the element must sit inside this selector (omit for site-wide)
  // areaIndex - the matched area must be the nth of its kind on the page
  // trigger   - the clicked element, or one of its ancestors, matches this selector
  // list      - product list the interaction belongs to, added to the GA4 payload
  // media     - also report the clicked image's url and alt text
  // on        - event type, defaults to click
  var RULES = [
    // Cart upsell. The drawer renders on every template, so it is matched before the
    // generic product card rules further down.
    {
      event: 'cart_upsell_add_to_cart',
      area: 'cart-upsell, #cartUpsellHolder',
      trigger: '.product-upsell__btn, [data-add-to-cart]',
      list: { id: 'cart_upsell', name: 'Cart upsell' }
    },
    {
      event: 'cart_upsell_click',
      area: 'cart-upsell, #cartUpsellHolder',
      trigger: 'a[href*="/products/"]',
      list: { id: 'cart_upsell', name: 'Cart upsell' }
    },

    // Search icon in the header (desktop and mobile). Matched before the menu rule, which
    // would otherwise claim it. Not named "search": GA4 already gets that event from the
    // Google & YouTube app when a search is run, and the two must not be counted together.
    { event: 'search_click', area: '.site-header, #nav-drawer-section',
      trigger: '.search-popdown__toggle, [data-popdown-toggle]' },

    // Header and mobile menu. The mobile menu is its own section (#nav-drawer-section),
    // outside the header. Opening and closing the drawer are not menu items.
    {
      event: 'menu_interaction',
      area: '.site-header, [data-mobile-menu], .mobile-menu, #nav-drawer-section',
      trigger: 'a[href], button:not([data-mobile-menu-trigger]):not(.drawer__close-button)'
    },

    // Homepage. Only the first carousel is in scope; the homepage renders several, and
    // areaIndex pins this to the first so the others stay silent.
    {
      event: 'homepage_hero_banner_cta', template: 'index',
      area: 'banner-image-component', areaIndex: 0,
      trigger: '.banner-image__btn'
    },
    { event: 'homepage_products_tabs', template: 'index',
      area: '.tab-collections, [id*="section-tab-collections"]',
      trigger: '.tabs__link, [data-tabs-link]' },
    {
      event: 'homepage_first_product_carousel_quick_buy',
      template: 'index',
      area: '[id^="shopify-section-"]:has(.featured-collection__container)', areaIndex: 0,
      trigger: '.product-grid-item__quick-buy, .btn--quick-buy, [data-add-to-cart]',
      list: { id: 'homepage_first_product_carousel', name: 'Homepage first carousel' }
    },
    {
      event: 'homepage_first_product_carousel_image_click',
      template: 'index',
      area: '[id^="shopify-section-"]:has(.featured-collection__container)', areaIndex: 0,
      trigger: '[data-product-image], .product__media__image', media: true,
      list: { id: 'homepage_first_product_carousel', name: 'Homepage first carousel' }
    },

    // Collection. Two sections can render the category strip, both configured per
    // collection, so neither renders unless someone has picked collections for it.
    { event: 'collections_tabs', template: 'collection',
      trigger: '.subcollection__image, a.list-collections__item__outer, .tabs__link, [data-tabs-link]' },
    // Card buttons only. For a product with variants the card button opens the quick view
    // popup (.pswp, outside the grid), whose own add to cart button would otherwise count
    // the same purchase a second time. The add itself is reported by add_to_cart.
    {
      event: 'collections_quick_buy',
      template: 'collection',
      trigger: '.product-grid-item__quick-buy, .btn--quick-buy',
      list: { id: collectionHandle || 'collection', name: collectionTitle || 'Collection' }
    },

    // Product upsell carousel at the end of the product page.
    {
      event: 'product_upsell_quick_buy',
      area: '.related-products, [id*="related-products"], .recently-viewed',
      trigger: '.product-grid-item__quick-buy, .btn--quick-buy, [data-add-to-cart]',
      list: { id: 'product_upsell', name: 'Product upsell' }
    },

    // Product page.
    { event: 'product_image_gallery_click', area: '[data-product-single-media-thumbs]',
      trigger: '.product-single__thumbnail, [data-thumbnail-id]', media: true },
    // The zoom lightbox link (a.product-gallery__media-link) is an empty overlay on top of
    // the image, so that is what a visitor actually clicks.
    { event: 'product_image_click', area: '[data-product-single-media-slider]',
      trigger: '.product-gallery__media-link, [data-product-single-media-wrapper] img, .product__media img',
      media: true },
    { event: 'product_reviews_rating_click', template: 'product',
      area: 'product-component',
      trigger: sel('reviews_badge', '[data-widget-name="preview_badge"]') },
    // Subscriptions come from Recharge, which fills .recharge-subscription-widget at
    // runtime - the theme's own selling-plan form is never rendered on this store, so
    // the option markup is the app's and the selector is correctable in theme settings.
    // Excludes the one-time option (value "onetime" in the current widget, empty in older
    // ones): reporting it as a subscribe click would be worse than not tracking it at all.
    // The widget renders inside a shadow root, which "change" never leaves - "input" does,
    // and like change it only fires when the selection actually moves.
    { event: 'product_subscribe_click', template: 'product', on: 'input',
      area: '.recharge-subscription-widget, [data-subscription-selectors]',
      trigger: sel('subscription_plan',
        'input[type="radio"]:not([value=""]):not([value="onetime"]):not([data-rc-one-time]), select') },
    { event: 'product_first_tabs', template: 'product',
      trigger: '.product__accordion__title' },
    // "Buy with" is the upsell block on the product page. complementary-products is the
    // Search & Discovery variant of the same idea, so both are covered.
    { event: 'product_buy_with_add_to_cart', area: '.upsell__products, .complementary-products',
      trigger: '.product-upsell__btn, [data-add-to-cart]' },
    // The section type keeps this off the cart notes and gift-note widgets, which use
    // the same collapsible trigger.
    { event: 'product_faqs', area: '[data-section-type="accordions"]',
      trigger: '[data-collapsible-trigger]' }
  ];

  // The GA4 item payload rendered by snippets/gtm-product_item.liquid. Nearest wins,
  // so an upsell card beats the product page it sits on.
  function itemFor(element) {
    var source = closest(element, '[data-track-item]');
    if (!source) return null;
    try {
      return JSON.parse(source.dataset.trackItem);
    } catch (e) {
      return null;
    }
  }

  // link_text is the visible label, so it changes per locale and is not safe to group
  // on; link_id and link_url survive translation.
  // "/en/products/x" -> "/products/x" so the same link groups across locales.
  function stripLocale(url) {
    if (!url || localeRoot === '/' || url.indexOf(localeRoot) !== 0) return url;
    return '/' + url.slice(localeRoot.length);
  }

  function identityFor(element) {
    var identity = {};
    if (element.tagName === 'SELECT' && element.value) {
      identity.link_id = element.value;
      return identity;
    }
    if (element.tagName === 'INPUT' && element.value) identity.link_id = element.value;
    var tagged = element.closest('[data-track-id]');
    if (tagged && tagged.dataset.trackId) identity.link_id = tagged.dataset.trackId;
    // Product media carries no handle, but the theme already stamps the Shopify media
    // id on each slide and thumbnail - enough to say which image was clicked.
    if (!identity.link_id) {
      // Slides and thumbnails both carry data-id (the Shopify media id). data-media-id
      // on a slide is "{section}-{media id}", so data-id is the one to report.
      var media = element.closest('[data-id], [data-media-id]');
      if (media) {
        identity.link_id = media.dataset.id || media.dataset.mediaId;
      }
    }
    var anchor = element.closest('a[href]');
    if (anchor) identity.link_url = stripLocale(anchor.getAttribute('href'));
    return identity;
  }

  function mediaFor(element) {
    var img = element.tagName === 'IMG' ? element : element.querySelector('img');
    // The zoom link is an overlay with no image inside it; the image is its sibling in
    // the same slide.
    if (!img) {
      var slide = element.closest('[data-product-single-media-wrapper], [data-id], [data-media-id]');
      img = slide && slide.querySelector('img');
    }
    if (!img) return null;
    var media = {};
    var url = img.getAttribute('data-original-src') || img.currentSrc || img.getAttribute('src');
    if (url) media.image_url = url;
    var alt = img.getAttribute('alt');
    if (alt) media.image_alt = alt;
    return media;
  }

  function labelFor(element) {
    // A radio or checkbox has no text of its own; its <label> carries it.
    if (element.tagName === 'INPUT') {
      var label = element.closest('label');
      if (label) return (label.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100);
    }
    if (element.tagName === 'SELECT') {
      var chosen = element.options[element.selectedIndex];
      return chosen ? (chosen.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100) : '';
    }
    var labelled = element.closest('[data-track-label]');
    if (labelled && labelled.dataset.trackLabel) {
      return labelled.dataset.trackLabel.slice(0, 100);
    }
    var card = element.closest('.product-grid-item, [data-product-block]');
    var title = card && card.querySelector('.product-grid-item__title');
    var source = title || element;
    var value = (source.textContent || '').replace(/\s+/g, ' ').trim();
    return (value || element.getAttribute('aria-label') || '').slice(0, 100);
  }

  function push(name, linkText, item, list, identity, media) {
    var payload = { event: name };
    if (linkText) payload.link_text = linkText;
    if (identity) {
      if (identity.link_id) payload.link_id = identity.link_id;
      if (identity.link_url) payload.link_url = identity.link_url;
    }
    if (media) {
      if (media.image_url) payload.image_url = media.image_url;
      if (media.image_alt) payload.image_alt = media.image_alt;
    }

    if (item) {
      var ecommerce = {};
      if (list) {
        item.item_list_id = list.id;
        item.item_list_name = list.name;
        ecommerce.item_list_id = list.id;
        ecommerce.item_list_name = list.name;
      }
      ecommerce.items = [item];
      payload.ecommerce = ecommerce;
      // Clear the previous ecommerce object so GA4 does not merge stale items in.
      window.dataLayer.push({ ecommerce: null });
    }

    window.dataLayer.push(payload);
    remember(payload);
  }

  // element.closest() that steps out through shadow hosts. App widgets such as Recharge
  // render inside a shadow root, and the areas and item data sit outside it.
  function closest(element, selector) {
    while (element) {
      var found = element.closest(selector);
      if (found) return found;
      var root = element.getRootNode && element.getRootNode();
      element = root && root.host;
    }
    return null;
  }

  // Capture phase on document: immune to any handler calling stopPropagation, works on
  // markup injected after load (the review widget, quick view, cart drawer re-renders),
  // and carries no dependency on the theme's own JS bundles. Inside a shadow root
  // event.target is retargeted to the host, so the real element comes from composedPath().
  function dispatch(event, type) {
    var target = event.composedPath ? event.composedPath()[0] : event.target;
    if (!target || !target.closest) return;

    for (var i = 0; i < RULES.length; i++) {
      var rule = RULES[i];
      if ((rule.on || 'click') !== type) continue;
      if (rule.template && rule.template !== template) continue;

      var element = target.closest(rule.trigger);
      if (!element) continue;
      if (rule.area && !closest(element, rule.area)) continue;
      if (rule.areaIndex !== undefined) {
        var scope = closest(element, rule.area);
        var peers = document.querySelectorAll(rule.area);
        if (Array.prototype.indexOf.call(peers, scope) !== rule.areaIndex) continue;
      }

      push(
        rule.event,
        labelFor(element),
        itemFor(element),
        rule.list,
        identityFor(element),
        rule.media ? mediaFor(element) : null
      );
      return;
    }
  }

  // --- Cart API events -------------------------------------------------------
  //
  // Shopify's native GA4 integration reports add_to_cart straight to GA4 and never
  // writes to dataLayer, so without this GTM sees the quick-buy click but never the
  // cart change behind it - no quantity, no value, no line item.
  //
  // The theme adds to cart with fetch(), so wrapping fetch catches every path: the
  // product form, quick buy, upsells and the cart drawer's own quantity controls.
  //
  // Named *_custom so it cannot be confused with the native add_to_cart if both are
  // running. Drop the suffix once you have decided which source GA4 should count.

  var currency = (window.theme && window.theme.current_iso_code) || '';

  // Built from the cart response alone. product_id, variant_id and sku come straight
  // from Shopify and are identical in every locale; the titles are display only.
  function cartItem(line) {
    var unit = line.final_price != null ? line.final_price : line.price;
    // Cart lines give prices in minor units (79900). The items_added / items_removed
    // entries of change.js give them as a major-unit string ("799.00") instead.
    unit = typeof unit === 'string' ? parseFloat(unit) : unit / 100;
    var item = {
      item_id: line.sku || String(line.variant_id),
      product_id: line.product_id,
      variant_id: line.variant_id,
      item_name: line.product_title || line.title,
      item_handle: line.handle,
      item_variant: line.variant_title,
      price: Math.round(unit * 100) / 100,
      quantity: line.quantity
    };
    if (currency) item.currency = currency;
    return item;
  }

  function pushCart(name, lines) {
    if (!lines || !lines.length) return;
    var items = [];
    var value = 0;
    for (var i = 0; i < lines.length; i++) {
      var item = cartItem(lines[i]);
      value += item.price * (item.quantity || 1);
      items.push(item);
    }
    window.dataLayer.push({ ecommerce: null });
    var payload = {
      event: name,
      ecommerce: { value: Math.round(value * 100) / 100, items: items }
    };
    if (currency) payload.ecommerce.currency = currency;
    window.dataLayer.push(payload);
    remember(payload);
  }

  // Quantity asked for per variant id, read from the add.js request body: FormData
  // (the theme's product form), URLSearchParams, a urlencoded string or JSON
  // ({id, quantity} or {items: [...]}). Null when the body can't be read.
  function requestedQuantities(body) {
    var entries = [];
    if (body && typeof body.getAll === 'function') {
      var ids = body.getAll('id');
      for (var i = 0; i < ids.length; i++) entries.push({ id: ids[i], quantity: body.get('quantity') });
    } else if (typeof body === 'string') {
      try {
        var parsed = JSON.parse(body);
        entries = parsed.items || [parsed];
      } catch (e) {
        return requestedQuantities(new URLSearchParams(body));
      }
    }
    if (!entries.length) return null;
    var quantities = {};
    for (var j = 0; j < entries.length; j++) {
      var id = String(entries[j].id);
      quantities[id] = (quantities[id] || 0) + (parseInt(entries[j].quantity, 10) || 1);
    }
    return quantities;
  }

  function handleCartResponse(url, json, body) {
    if (!json) return;
    if (/\/cart\/add/.test(url)) {
      // add.js returns the added line, or {items:[...]} for a multi-add. Each line
      // carries the line's new total quantity, so an add to a variant already in the
      // cart would report the whole line - report what was asked for instead.
      var lines = json.items || [json];
      var requested = requestedQuantities(body);
      var added = [];
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        var quantity = requested && requested[String(line.variant_id)];
        added.push(quantity ? Object.assign({}, line, { quantity: quantity }) : line);
      }
      pushCart('add_to_cart_custom', added);
      return;
    }
    // change.js and update.js return the whole cart plus what moved.
    if (json.items_added && json.items_added.length) {
      pushCart('add_to_cart_custom', json.items_added);
    }
    if (json.items_removed && json.items_removed.length) {
      pushCart('remove_from_cart_custom', json.items_removed);
    }
  }

  (function interceptCart() {
    if (typeof window.fetch !== 'function') return;
    var original = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var body = init && init.body;
      var result = original.apply(this, arguments);
      if (!/\/cart\/(add|change|update)/.test(url)) return result;
      return result.then(function (response) {
        // Read a clone so the theme still gets an unconsumed body, and never let a
        // tracking failure surface as a cart failure.
        try {
          response.clone().json().then(function (json) {
            try { handleCartResponse(url, json, body); } catch (e) {}
          }, function () {});
        } catch (e) {}
        return response;
      });
    };
  })();

  document.addEventListener('click', function (event) { dispatch(event, 'click'); }, true);
  document.addEventListener('input', function (event) { dispatch(event, 'input'); }, true);

  // Search results page view. Skipped when no query was entered.
  if (template === 'search') {
    var searchTerm = queryFlag('q').trim();
    if (searchTerm) {
      var searchPayload = { event: 'view_search_results', search_term: searchTerm.slice(0, 100) };
      window.dataLayer.push(searchPayload);
      remember(searchPayload);
    }
  }

  replay();
})();
